import React, { createContext, useContext } from 'react';
import { useProducts, UseProducts, UseProductsProps } from '../hooks/useProducts/useProducts';

export const ProductContext = createContext<UseProducts>({
  products: [],
  numberOfProducts: 0,
  isLoading: false,
});

export const ProductProvider: React.VFC<UseProductsProps & { children: React.ReactNode }> = ({ children, ...rest }) => {
  const value = useProducts(rest);
  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

export type WithProductProviderProps<T> = T & UseProductsProps;

export const withProductProvider =
  <T extends Record<string, any>>(Component: React.ComponentType<T>) =>
  // eslint-disable-next-line react/display-name
  ({
    productVariantIds,
    algoliaQuery,
    productSkus,
    products,
    googleRecAiModel,
    googleRecNumberOfProducts,
    isRecentlyViewed,
    options,
    algoliaSearchOptions,
    source,
    ...props
  }: WithProductProviderProps<T>) =>
    (
      <ProductProvider
        productVariantIds={productVariantIds}
        algoliaQuery={algoliaQuery}
        productSkus={productSkus}
        products={products}
        googleRecAiModel={googleRecAiModel}
        googleRecNumberOfProducts={googleRecNumberOfProducts}
        isRecentlyViewed={isRecentlyViewed}
        algoliaSearchOptions={algoliaSearchOptions}
        options={options}
        source={source}
      >
        <Component {...(props as T)} />
      </ProductProvider>
    );

export const useProductContext = (): UseProducts => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }

  return context;
};
